/**
 * GraphQL queries and mutations for the Integrations page (ShelterLuv).
 */

import { gql } from '@apollo/client';

/** Fetch the current user's ShelterLuv configuration status. Returns null if not configured. */
export const rescueShelterluvStatusQuery = gql`
  query rescueShelterluvStatus {
    rescueShelterluvStatus {
      enabled
      apiKeyEndsWith
      lastImportAt
      thirdPartyChipIncrementalEnabled
      thirdPartyChipBackfillEnabled
      emailUpdateOptIn
    }
  }
`;

export interface RescueShelterluvStatusData {
  rescueShelterluvStatus: {
    enabled: boolean;
    apiKeyEndsWith: string;
    lastImportAt: string | null;
    thirdPartyChipIncrementalEnabled: boolean;
    thirdPartyChipBackfillEnabled: boolean;
    emailUpdateOptIn: boolean;
  } | null;
}

/** Fetch the current user's reseller info including shelterluv chip stats. */
export const rescueResellerStatsQuery = gql`
  query rescueResellerStats {
    currentUser {
      id
      chipReseller {
        id
        name
        stats {
          chipsRegisteredOnShelterluv
        }
      }
    }
  }
`;

export interface RescueResellerStatsData {
  currentUser: {
    id: string;
    chipReseller: {
      id: string;
      name: string;
      stats: {
        chipsRegisteredOnShelterluv: number;
      };
    } | null;
  };
}

/** Enable ShelterLuv integration with an API key and sync options. */
export const rescueEnableShelterluvMutation = gql`
  mutation rescueEnableShelterluv($input: RescueEnableShelterluvInput!) {
    rescueEnableShelterluv(input: $input) {
      id
      name
      preferences {
        shelterluv {
          enabled
          apiKeyEndsWith
          lastImportAt
          thirdPartyChipBackfillEnabled
          thirdPartyChipIncrementalEnabled
        }
      }
      stats {
        chipsRegisteredOnShelterluv
      }
    }
  }
`;

export interface RescueEnableShelterluvData {
  rescueEnableShelterluv: {
    id: string;
    name: string;
    preferences: {
      shelterluv: {
        enabled: boolean;
        apiKeyEndsWith: string;
        lastImportAt: string | null;
        thirdPartyChipBackfillEnabled: boolean;
        thirdPartyChipIncrementalEnabled: boolean;
      };
    };
    stats: {
      chipsRegisteredOnShelterluv: number;
    };
  };
}

/** Disable ShelterLuv integration. */
export const rescueDisableShelterluvMutation = gql`
  mutation rescueDisableShelterluv {
    rescueDisableShelterluv {
      id
      preferences {
        shelterluv {
          enabled
        }
      }
    }
  }
`;

export interface RescueDisableShelterluvData {
  rescueDisableShelterluv: {
    id: string;
    preferences: {
      shelterluv: {
        enabled: boolean;
      } | null;
    };
  };
}

/**
 * Opt the rescue in or out of email updates for registrations imported from ShelterLuv.
 * Selects the full shelterluv preferences object so the normalized ChipReseller cache
 * entry written by rescueEnableShelterluv is not clobbered with a narrower object.
 */
export const rescueSetShelterluvEmailUpdateOptInMutation = gql`
  mutation rescueSetShelterluvEmailUpdateOptIn($optIn: Boolean!) {
    rescueSetShelterluvEmailUpdateOptIn(optIn: $optIn) {
      id
      preferences {
        shelterluv {
          enabled
          apiKeyEndsWith
          lastImportAt
          thirdPartyChipBackfillEnabled
          thirdPartyChipIncrementalEnabled
          emailUpdateOptIn
        }
      }
    }
  }
`;

export interface RescueSetShelterluvEmailUpdateOptInData {
  rescueSetShelterluvEmailUpdateOptIn: {
    id: string;
    preferences: {
      shelterluv: {
        enabled: boolean;
        apiKeyEndsWith: string;
        lastImportAt: string | null;
        thirdPartyChipBackfillEnabled: boolean;
        thirdPartyChipIncrementalEnabled: boolean;
        emailUpdateOptIn: boolean;
      } | null;
    };
  };
}

// ── Manual CSV import ──────────────────────────
//
// Three-step handoff:
//   1. `rescueGetImportUploadUrl` — ask the backend for a short-lived
//      presigned PUT URL, then PUT the CSV bytes directly to S3.
//   2. `rescueStartImport` — once the PUT succeeds, hand the object key
//      off to a worker. Returns a progress row (initially QUEUED).
//   3. `rescueCsvImportProgress` — poll the returned id until `status`
//      reaches a terminal state to track the worker's progress.

/**
 * Request a presigned S3 PUT URL for a CSV upload. The returned `url` is
 * short-lived; the client must PUT the file body with the `Content-Type`
 * the `contentType` enum resolves to (`TEXT_CSV` → `text/csv`) — S3
 * enforces an exact header match against the signed request. `key` is the
 * resulting object key, which is then passed to `rescueStartImport`.
 */
export const rescueGetImportUploadUrlQuery = gql`
  query rescueGetImportUploadUrl($input: RescueGetImportUploadUrlInput!) {
    rescueGetImportUploadUrl(input: $input) {
      url
      key
      expiration
    }
  }
`;

/** File format the client will upload. The backend maps each enum member
 * to a MIME type the signed PUT must match (`TEXT_CSV` → `text/csv`). */
export type RescueImportContentType = 'TEXT_CSV';

export interface RescueGetImportUploadUrlInput {
  contentType: RescueImportContentType;
}

export interface RescueGetImportUploadUrlData {
  rescueGetImportUploadUrl: {
    url: string;
    key: string;
    expiration: string;
  };
}

/**
 * Lifecycle of a rescue CSV import job.
 *   QUEUED / RUNNING       — in flight; keep polling.
 *   SUCCEEDED              — worker finished; `totalRows` + `final*` populated.
 *   FAILED                 — worker errored; `terminalError` populated.
 */
export type RescueCsvImportStatus = 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED';

const TERMINAL_IMPORT_STATUSES: ReadonlySet<RescueCsvImportStatus> = new Set(['SUCCEEDED', 'FAILED']);

/** True once the worker has reached a terminal state — polling can stop. */
export function isTerminalImportStatus(status: RescueCsvImportStatus): boolean {
  return TERMINAL_IMPORT_STATUSES.has(status);
}

/**
 * Live phase of an in-flight import, read from the worker's job progress
 * (not Postgres). Null once the job is terminal and has aged out of the
 * queue.
 *   PARSING    — reading + validating rows out of the CSV.
 *   APPLYING   — turning rows into pet records.
 *   FINALIZING — wrapping up after apply.
 */
export type RescueCsvImportPhase = 'PARSING' | 'APPLYING' | 'FINALIZING';

/**
 * Progress snapshot for a rescue CSV import job. Mirrors the
 * `RescueCsvImportProgress` GQL type.
 *
 * Two tiers of counters:
 *   - Durable (`totalRows`, `finalProcessed`, `finalFailed`) — written to
 *     Postgres when the job settles; null until then.
 *   - Live (`phase`, `rowsSeen`, `rowsProcessed`, `rowsFailed`,
 *     `errors`) — best-effort, read off the running BullMQ job;
 *     null/empty once there's no live job (queued, or terminal + aged out).
 *
 * `terminalError` is set only when `status` is FAILED.
 */
export interface RescueCsvImportProgress {
  id: string;
  status: RescueCsvImportStatus;
  totalRows: number | null;
  finalProcessed: number | null;
  finalFailed: number | null;
  terminalError: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  phase: RescueCsvImportPhase | null;
  rowsSeen: number | null;
  rowsProcessed: number | null;
  rowsFailed: number | null;
  errors: string[];
}

/**
 * Hand a freshly-uploaded CSV off to the import worker. Pass the `key`
 * returned by `rescueGetImportUploadUrl` (after the S3 PUT succeeds).
 * Returns a progress row — initially QUEUED — whose `id` is then polled
 * via `rescueCsvImportProgressQuery`. Idempotent on `uploadedFileKey`:
 * calling twice with the same key returns the existing row.
 */
export const rescueStartImportMutation = gql`
  mutation rescueStartImport($input: RescueStartImportInput!) {
    rescueStartImport(input: $input) {
      id
      status
      totalRows
      finalProcessed
      finalFailed
      terminalError
      createdAt
      startedAt
      completedAt
      phase
      rowsSeen
      rowsProcessed
      rowsFailed
      errors
    }
  }
`;

export interface RescueStartImportInput {
  /** S3 object key returned by `rescueGetImportUploadUrl`. */
  uploadedFileKey: string;
}

export interface RescueStartImportData {
  rescueStartImport: RescueCsvImportProgress;
}

/**
 * Look up the progress of a started import by id. Returns null if no
 * import with that id exists for the caller. Clients poll this until
 * `status` is terminal (see `isTerminalImportStatus`).
 */
export const rescueCsvImportProgressQuery = gql`
  query rescueCsvImportProgress($id: ID!) {
    rescueCsvImportProgress(id: $id) {
      id
      status
      totalRows
      finalProcessed
      finalFailed
      terminalError
      createdAt
      startedAt
      completedAt
      phase
      rowsSeen
      rowsProcessed
      rowsFailed
      errors
    }
  }
`;

export interface RescueCsvImportProgressData {
  rescueCsvImportProgress: RescueCsvImportProgress | null;
}
